import React, { memo, useContext } from 'react';

import { AppContext } from 'src/components/app-context';
import { PanoramaAdInContentExperimentType } from 'src/hooks/experiments';
import AdContainer from '../../ads';
import { withAds } from '../../ads/context/withAds';
import { AdPlacement } from '../../ads/types';
import * as S from './styles';
import { useIsKWLP } from 'src/areas/keyword-landing-page/contexts';

const topAdSpacing = { top: '10px' };
const bottomAdSpacing = { bottom: '10px' };
const renderStaticOutsiderAds = (
  numberOfAds: number,
  trailingSkyscraper?: boolean
) =>
  [
    <S.SkyscraperAd
      key="skyscraper_1"
      placement="skyscraper_1"
      format="widescreen_1"
      min={1199}
      max={10000}
      scalable
      scaleBase=".outsider-ads"
    />,
    <S.SkyscraperAd
      key="skyscraper_2"
      placement="skyscraper_2"
      format="widescreen_2"
      min={1199}
      max={10000}
      scalable
      scaleBase=".outsider-ads"
      sticky={trailingSkyscraper}
    />,
    <S.SkyscraperAd
      key="skyscraper_3"
      placement="skyscraper_3"
      format="widescreen_3"
      min={1199}
      max={10000}
      scalable
      scaleBase=".outsider-ads"
    />,
  ].slice(0, numberOfAds);

type Props = {
  className?: string;
  children: React.ReactNode;
  topPlacements: PlacementType[];
  outsiderPlacements: PlacementType[];
  bottomPlacements: PlacementType[];
  withAds: boolean;
  headerHeight: number;
  adsDelayed: object;
  wideContent: boolean;
  'data-test': string;
  adServiceContext: AdServiceContextT;
  alignLeft?: boolean;
  isWide?: boolean;
  trailingSkyscraper: boolean;
  panoramaAdVariant: PanoramaAdInContentExperimentType;
};

type PlacementType = {
  placement: AdPlacement;
  min: number;
  max: number;
};

export type TakeoverSettings = {
  width: number;
  height: number;
  offgrid: boolean;
  topMargin: number;
};

export type AdServiceContextT = {
  takeover?: TakeoverSettings;
};

const PageContent = (props: Props) => {
  const {
    className,
    topPlacements,
    bottomPlacements,
    children,
    adsDelayed,
    wideContent,
    adServiceContext,
    alignLeft,
    isWide,
    trailingSkyscraper,
  } = props;

  const { market } = useContext(AppContext);
  const isKWLP = useIsKWLP();
  const adsActive = market && market.ads && market.ads.active;
  const showAds = props.withAds && adsActive;
  const takeover =
    showAds && adServiceContext && adServiceContext.takeover
      ? adServiceContext.takeover
      : undefined;

  return (
    <S.PageContent
      data-test={props['data-test']}
      className={className}
      isWide={isWide}
      // We only want no max width if we're on a kwlp
      fixedMaxWidth={!isKWLP}
    >
      <S.Wrapper>
        {showAds && topPlacements.length > 0 && (
          <S.TopAds takeover={takeover} className="top-ads">
            {topPlacements.map((adProps, index) => {
              return (
                <AdContainer
                  {...adProps}
                  key={`${adProps.placement}-${index.toString()}`}
                  isReady={adsDelayed[adProps.placement]}
                  spacing={takeover ? {} : topAdSpacing}
                />
              );
            })}
          </S.TopAds>
        )}
        <S.Content
          takeover={takeover}
          hasAds={adsActive}
          wideContent={wideContent}
          alignLeft={alignLeft}
          isWide={isWide}
          shouldSetMaxWidth={!isKWLP}
        >
          {children}
        </S.Content>

        {showAds && bottomPlacements.length > 0 && (
          <aside className="bottom-ads">
            {bottomPlacements.map(adProps => (
              <AdContainer
                {...adProps}
                key={adProps.placement}
                isReady={adsDelayed[adProps.placement]}
                spacing={bottomAdSpacing}
              />
            ))}
          </aside>
        )}
      </S.Wrapper>

      {showAds && (
        <S.OutsiderAds takeover={takeover} className="outsider-ads">
          {renderStaticOutsiderAds(3, trailingSkyscraper)}
        </S.OutsiderAds>
      )}
    </S.PageContent>
  );
};

PageContent.defaultProps = {
  children: null,
  topPlacements: [
    {
      scalable: true,
      formats: ['toppanorama_wde', 'wallpaper'],
      placement: 'panorama_top',
      min: 861,
      max: 10000,
    },
  ],
  outsiderPlacements: [
    {
      placement: 'skyscraper_1',
      format: 'widescreen_1',
      min: 1199,
      max: 10000,
      scalable: true,
      scaleParent: '.outsider-ads',
    },
    {
      placement: 'skyscraper_2',
      format: 'widescreen_2',
      min: 1199,
      max: 10000,
      scalable: true,
      scaleParent: '.outsider-ads',
    },
    {
      placement: 'skyscraper_3',
      format: 'widescreen_3',
      min: 1199,
      max: 10000,
      scalable: true,
      scaleParent: '.outsider-ads',
    },
  ],
  bottomPlacements: [
    {
      placement: 'panorama_footer',
      format: 'panorama_4_wde',
      min: 1024,
      max: 10000,
      triggerPoint: 100,
    },
    {
      placement: 'dubbelpanorama3',
      format: 'panorama_3',
      min: 0,
      max: 860,
      triggerPoint: 100,
    },
  ],
  withAds: false,
  headerHeight: undefined,
  adsDelayed: {},
  wideContent: false,
  'data-test': undefined,
};

export default memo(withAds(PageContent));
